import { useCallback, useEffect, useState } from 'react';

/**
 * Le **vrai** plein écran : celui du navigateur.
 *
 * « Quand je t'ai dit plein écran, c'est plein écran du navigateur […] pour que le site soit
 * vraiment en plein écran, que je voie pas les trucs de Google Chrome ou autre. » Masquer la barre
 * de menu et le dock ne suffisait pas : la barre d'adresse et les onglets restaient à l'écran,
 * juste au moment où l'enseignant projette devant sa classe. Il faut donc appeler l'API Fullscreen
 * (`requestFullscreen` / `exitFullscreen`), et elle impose trois choses :
 *
 * 1. **Un vrai clic.** Les navigateurs refusent `requestFullscreen()` hors d'un geste de
 *    l'utilisateur. `toggle` est donc appelé par les gestionnaires `onClick` des deux commandes
 *    (la pastille verte de la barre de titre et le bouton de la barre de menu), jamais par un
 *    effet.
 * 2. **Le document est la seule source de vérité.** L'état n'est jamais posé « en avance » :
 *    `active` ne fait que refléter `document.fullscreenElement`, et `fullscreenchange` le
 *    resynchronise. C'est ce qui fait que la touche Échap du navigateur — que le navigateur
 *    consomme, sans jamais la donner à la page — remet l'interface dans son état normal.
 * 3. **Un refus ne coince rien.** Si le navigateur rejette la demande (permission, plein écran
 *    déjà pris par un autre élément, `iframe` sans autorisation), la promesse échoue, aucun
 *    `fullscreenchange` n'arrive, `active` reste `false` : l'interface reste entière, elle ne
 *    reste pas à moitié en plein écran sans barre de menu ni dock.
 */
function isDocumentFullscreen(): boolean {
  return document.fullscreenElement !== null;
}

export interface BrowserFullscreen {
  /** `true` seulement si le document EST en plein écran, jamais parce qu'on l'a demandé. */
  active: boolean;
  /** À appeler depuis un `onClick`, jamais depuis un effet : le geste doit venir de la personne. */
  toggle: () => void;
  /** Sortie explicite (fermer la fenêtre, la réduire) — sans effet si on n'y est pas. */
  exit: () => void;
}

export function useBrowserFullscreen(): BrowserFullscreen {
  const [active, setActive] = useState(isDocumentFullscreen);

  useEffect(() => {
    const sync = (): void => {
      setActive(isDocumentFullscreen());
    };
    // `fullscreenchange` couvre TOUTES les sorties : notre bouton, la touche Échap du navigateur,
    // le raccourci système, un changement d'onglet. S'en remettre à notre seul état interne
    // laisserait le bureau croire qu'il est encore en plein écran, sans barre de menu ni dock.
    document.addEventListener('fullscreenchange', sync);
    return () => {
      document.removeEventListener('fullscreenchange', sync);
    };
  }, []);

  const exit = useCallback(() => {
    if (!isDocumentFullscreen()) return;
    void document.exitFullscreen().catch(() => {
      setActive(isDocumentFullscreen());
    });
  }, []);

  const toggle = useCallback(() => {
    if (isDocumentFullscreen()) {
      exit();
      return;
    }
    // Tout le document, pas la fenêtre : le fond d'écran, la barre de menu et le dock doivent
    // rester dedans — l'enseignant projette le bureau, pas seulement l'app ouverte.
    void document.documentElement.requestFullscreen().catch(() => {
      setActive(isDocumentFullscreen());
    });
  }, [exit]);

  return { active, toggle, exit };
}
